import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Redeem as RedeemIcon,
  Search as SearchIcon,
  EmojiEvents as WinIcon,
  SentimentDissatisfied as LoseIcon,
  CheckCircle as CheckIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import LotteryBall from '../components/LotteryBall';
import { getLotteryList } from '../services/lotteryApi';
import { calculatePrize } from '../utils/lotteryUtils';
import Navigation from '../components/Navigation';

/**
 * 双色球兑奖页面
 */
function LotteryRedeemPage() {
  const history = useHistory();
  const [orders, setOrders] = useState([]);
  const [searchOrderId, setSearchOrderId] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [drawResult, setDrawResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);

  // 加载历史订单
  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getLotteryList();
      if (result.success) {
        // 转换字段名以匹配页面预期
        const orders = (result.data?.list || []).map(order => ({
          ...order,
          orderId: order.orderNo,
          createTime: order.betTime,
          statusText: order.status === 'pending' ? '待开奖' : order.status === 'won' ? '已中奖' : '未中奖',
          status: order.status === 'pending' ? 0 : order.status === 'won' ? 2 : 1
        }));
        setOrders(orders);
      }
    } catch (err) {
      console.error('加载订单失败:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  // 模拟开奖结果（实际应该从API获取）
  const mockDrawResult = {
    period: '2026052',
    redBalls: ['01', '05', '12', '18', '25', '32'],
    blueBall: '07',
    drawTime: '2026-05-08 21:15:00',
    prizePool: '856,234,567.89'
  };

  // 搜索订单
  const handleSearch = () => {
    if (!searchOrderId.trim()) return;
    
    setSearching(true);
    setTimeout(() => {
      const found = orders.find(o => o.orderId === searchOrderId.trim());
      if (found) {
        setSelectedOrder(found);
        setDrawResult(mockDrawResult);
      } else {
        setSelectedOrder(null);
        setDrawResult(null);
      }
      setSearching(false);
    }, 500);
  };

  // 选择历史订单
  const handleSelectOrder = (order) => {
    setSelectedOrder(order);
    setDrawResult(mockDrawResult);
    setSearchOrderId('');
  };

  // 计算中奖结果
  const getPrizeResult = () => {
    if (!selectedOrder || !drawResult) return null;
    
    return calculatePrize(
      selectedOrder.redBalls,
      selectedOrder.blueBall,
      drawResult.redBalls,
      drawResult.blueBall
    );
  };

  const prizeResult = getPrizeResult();

  return (
    <Box>
      {/* 系统导航栏 */}
      <Navigation />
      
      {/* 页面内容 */}
      <Box sx={{ maxWidth: 500, mx: 'auto', p: 2, minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* 页面标题 */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <RedeemIcon sx={{ fontSize: 48, color: '#E53935', mb: 1 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#E53935' }}>
          双色球兑奖
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          输入订单号或选择历史订单进行兑奖
        </Typography>
      </Box>

      {/* 搜索框 */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="请输入订单号"
            value={searchOrderId}
            onChange={(e) => setSearchOrderId(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            size="small"
          />
          <Button
            variant="contained"
            startIcon={searching ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />}
            onClick={handleSearch}
            disabled={searching || !searchOrderId.trim()}
            sx={{
              bgcolor: '#E53935',
              '&:hover': { bgcolor: '#C62828' },
              minWidth: 100
            }}
          >
            查询
          </Button>
        </Box>
      </Paper>

      {/* 开奖结果展示 */}
      {drawResult && (
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'linear-gradient(135deg, #E53935 0%, #C62828 100%)' }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', textAlign: 'center', mb: 1 }}>
            第 {drawResult.period} 期 开奖结果
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {drawResult.redBalls.map(num => (
              <LotteryBall key={num} number={num} type="red" selected size="small" />
            ))}
            <LotteryBall number={drawResult.blueBall} type="blue" selected size="small" />
          </Box>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'block', textAlign: 'center' }}>
            开奖时间：{drawResult.drawTime} | 奖池：¥{drawResult.prizePool}
          </Typography>
        </Paper>
      )}

      {/* 中奖结果 */}
      {prizeResult && selectedOrder && (
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
              {prizeResult.level > 0 ? (
                <>
                  <WinIcon sx={{ fontSize: 40, color: '#FFD700', mr: 1 }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFD700' }}>
                      恭喜中奖！
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#E53935' }}>
                      ¥ {prizeResult.prize.toLocaleString()}
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <LoseIcon sx={{ fontSize: 40, color: '#999', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#666' }}>
                    很遗憾，未中奖
                  </Typography>
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* 订单号码与开奖号码对比 */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                您的号码
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {selectedOrder.redBalls.map(num => (
                  <LotteryBall
                    key={num}
                    number={num}
                    type="red"
                    selected={drawResult.redBalls.includes(num)}
                    size="small"
                  />
                ))}
                <LotteryBall
                  number={selectedOrder.blueBall}
                  type="blue"
                  selected={drawResult.blueBall === selectedOrder.blueBall}
                  size="small"
                />
              </Box>
            </Box>

            {/* 中奖详情 */}
            {prizeResult.level > 0 && (
              <Box sx={{ bgcolor: '#FFF8E1', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  中奖详情
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>命中红球</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {prizeResult.redMatch} 个
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: '#666' }}>命中蓝球</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {prizeResult.blueMatch ? '是' : '否'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Chip
                      label={prizeResult.levelName}
                      color="warning"
                      size="small"
                      icon={<CheckIcon />}
                      sx={{ mt: 1 }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* 订单信息 */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" sx={{ color: '#666' }}>
                订单号：{selectedOrder.orderId} | 投注时间：{selectedOrder.createTime}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* 历史订单列表 */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 0.5, fontSize: 18 }} />
          历史订单
        </Typography>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : orders.length === 0 ? (
          <Paper sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#999' }}>
              暂无历史订单
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {orders.slice(0, 10).map(order => (
              <Grid item xs={12} key={order.orderId}>
                <Paper
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    border: selectedOrder?.orderId === order.orderId ? '2px solid #E53935' : 'none',
                    '&:hover': { bgcolor: '#f0f0f0' }
                  }}
                  onClick={() => handleSelectOrder(order)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      订单号：{order.orderId}
                    </Typography>
                    <Chip
                      label={order.statusText || '待开奖'}
                      size="small"
                      color={order.status === 2 ? 'success' : 'default'}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.3, flexWrap: 'wrap' }}>
                    {order.redBalls.map(num => (
                      <LotteryBall key={num} number={num} type="red" selected size="tiny" />
                    ))}
                    <LotteryBall number={order.blueBall} type="blue" selected size="tiny" />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 0.5 }}>
                    {order.createTime}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* 底部提示 */}
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography variant="caption">
          💡 提示：中奖金额超过1万元需携带身份证到福彩中心兑奖，1万元以下可在任意投注站兑奖。
        </Typography>
      </Alert>
    </Box>
    </Box>
  );
}

export default LotteryRedeemPage;
